import React from "react";
import PropTypes from "prop-types";
import { range } from "lodash";

import {
  makeStyles,
  TableBody as OriginalTableBody,
  TableCell,
  TableRow,
} from "@material-ui/core";
import Skeleton from "@material-ui/lab/Skeleton";
import { Checkbox } from "semantic-ui-react";

const TableBodySkeleton = ({ rowsLength, columnLength }) => {
  return range(rowsLength || 5).map((n) => {
    return (
      <TableRow hover tabIndex={-1} key={`sk_${n}_key`}>
        {range(columnLength).map((column) => {
          return (
            <TableCell component="td" key={`sk_${n}-column-${column}`}>
              <Skeleton variant="text" />
            </TableCell>
          );
        })}
      </TableRow>
    );
  });
};

TableBodySkeleton.propTypes = {
  columnLength: PropTypes.number,
  rowsLength: PropTypes.number,
};

TableBodySkeleton.defaultProps = { rowsLength: 5 };

const useStyles = makeStyles(() => {
  return {
    bodyCell: {
      fontSize: "0.875rem",
    },
    emptyDataBodyCell: {
      fontSize: "0.875rem",
      border: "none",
    },
    loadingRow: {
      opacity: "0.5",
      pointerEvents: "none",
    },
  };
});

function TableBody({
  isLoading,
  isReloading,
  columns,
  data,
  onRowClick,
  emptyDataMessage,
  checkSelected,
  ...props
}) {
  const classes = useStyles();

  if (isLoading) {
    return (
      <OriginalTableBody data-test="loading-table-body">
        <TableBodySkeleton
          columnLength={checkSelected ? columns.length + 1 : columns.length}
          rowsLength={data?.length}
        />
      </OriginalTableBody>
    );
  }

  if (!data || data.length === 0) {
    return (
      <OriginalTableBody data-test="empty-table-body">
        <TableRow>
          <TableCell
            className={classes.emptyDataBodyCell}
            data-test="empty-data-cell"
            colSpan={columns.length}
            {...props}
          >
            {emptyDataMessage}
          </TableCell>
        </TableRow>
      </OriginalTableBody>
    );
  }

  return (
    <OriginalTableBody data-test="table-body">
      {data.map((row) => {
        return (
          <TableRow
            hover
            tabIndex={-1}
            key={row.id}
            onClick={() => {
              if (onRowClick && !isReloading) onRowClick(row);
            }}
            style={{
              cursor: onRowClick && !isReloading ? "pointer" : "default",
            }}
            className={isReloading ? classes.loadingRow : ""}
          >
            {checkSelected && (
              <TableCell padding="checkbox">
                <Checkbox color="primary" checked={checkSelected(row)} />
              </TableCell>
            )}
            {columns.map((column) => {
              return (
                <TableCell
                  component="td"
                  scope="row"
                  key={`${row.id}-column-${column.id}`}
                  className={classes.bodyCell}
                  style={column.style}
                  {...props}
                >
                  {row[column.id]}
                </TableCell>
              );
            })}
          </TableRow>
        );
      })}
    </OriginalTableBody>
  );
}

TableBody.propTypes = {
  isLoading: PropTypes.bool,
  isReloading: PropTypes.bool,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
    })
  ).isRequired,
  data: PropTypes.array.isRequired,
  onRowClick: PropTypes.func,
  emptyDataMessage: PropTypes.string,
  checkSelected: PropTypes.func,
};

TableBody.defaultProps = {
  isLoading: false,
  isReloading: false,
  onRowClick: null,
  emptyDataMessage: "No data",
  checkSelected: null,
};

export default TableBody;
